import {
  formatEur,
  taxLabel,
  vatTotals,
  type BillingDTO,
  type CustomerDTO,
  type InvoiceDocDTO,
  type OrderDTO,
  type OrderItemDTO,
  type PartDTO,
  type TaskDTO,
  type VehicleDTO,
} from '@wf/shared'
import { formatDateDE } from '../time/format'

/**
 * Normalisiertes Beleg-Modell — die EINE Datenform, aus der Rechnung, Kostenvoranschlag UND Stornorechnung
 * gerendert werden. Zwei Adapter füllen es: {@link liveDocModel} (unverbindliche Vorschau aus dem LIVE-Auftrag)
 * und {@link snapshotDocModel} (eingefrorener Ledger-Beleg). So gibt es genau EIN Render-Layout (kein Drift).
 */
export interface BillingDocModel {
  docLabel: 'Rechnung' | 'Kostenvoranschlag' | 'Stornorechnung'
  invoiceNumber: string | null
  issueDate: string | null
  /** true = Rechnungs-ENTWURF (Wasserzeichen + Banner). Nur für die Live-Vorschau vor dem Festschreiben. */
  draft: boolean
  seller: BillingDTO | null
  customerName: string | null
  customerAddress: string | null
  subject: { orderNr: string | null; plate: string | null; vehicleModel: string | null; mileage: number | null; tasks: string[] }
  lines: { key: string; description: string; articleNumber: string | null; quantity: number; unitPriceNet: number; vatRate: number; lineNet: number }[]
  totals: { netCents: number; byRate: { rate: number; netCents: number; vatCents: number }[]; grossCents: number }
  kleinunternehmer: boolean
  /** Stornorechnung → Nummer der aufgehobenen Original-Rechnung. */
  relatedInvoiceNumber?: string | null
}

/** Live-Vorschau aus dem aktuellen Auftrag (Werte referenzieren die Live-Tabellen — beliebig oft neu). */
export function liveDocModel(opts: {
  kind: 'invoice' | 'estimate'
  order: OrderDTO
  tasks: TaskDTO[]
  parts: PartDTO[]
  items: OrderItemDTO[]
  billing: BillingDTO | null
  customer: CustomerDTO | null
  vehicle: VehicleDTO | null
  draft: boolean
}): BillingDocModel {
  const kleinunternehmer = opts.billing?.kleinunternehmer === 1
  const lines = [
    ...opts.parts.map((p) => ({ key: `p${p.id}`, description: p.name, articleNumber: p.partNumber, quantity: p.qty, unitPriceNet: p.priceCents, vatRate: p.vatRate, lineNet: p.priceCents * p.qty })),
    ...opts.items.map((i) => ({ key: `i${i.id}`, description: i.name, articleNumber: null, quantity: i.qty, unitPriceNet: i.priceCents, vatRate: i.vatRate, lineNet: i.priceCents * i.qty })),
  ]
  const t = vatTotals(lines.map((l) => ({ netCents: l.unitPriceNet, qty: l.quantity, vatRate: l.vatRate })), kleinunternehmer)
  return {
    docLabel: opts.kind === 'invoice' ? 'Rechnung' : 'Kostenvoranschlag',
    invoiceNumber: opts.kind === 'invoice' ? opts.order.invNo : null,
    issueDate: opts.kind === 'invoice' ? opts.order.invoicedAt : null,
    draft: opts.draft,
    seller: opts.billing,
    customerName: opts.customer?.name ?? null,
    customerAddress: opts.customer?.address ?? null,
    subject: { orderNr: opts.order.nr, plate: opts.vehicle?.plate ?? null, vehicleModel: opts.vehicle?.model ?? null, mileage: opts.order.mileage, tasks: opts.tasks.map((task) => task.text) },
    lines,
    totals: { netCents: t.netCents, byRate: t.byRate, grossCents: t.grossCents },
    kleinunternehmer,
  }
}

/** Eingefrorener Ledger-Beleg (Rechnung oder Stornorechnung) — Werte kommen ausschließlich aus dem Snapshot. */
export function snapshotDocModel(inv: InvoiceDocDTO): BillingDocModel {
  const kleinunternehmer = inv.taxScheme === 'kleinunternehmer'
  const byRateMap = new Map<number, number>()
  for (const l of inv.lines) byRateMap.set(l.vatRate, (byRateMap.get(l.vatRate) ?? 0) + l.lineNet)
  // USt je Satz-Gruppe aus den eingefrorenen Netto-Summen (gleiche Rundung wie beim Festschreiben; das
  // Vorzeichen bleibt erhalten → Stornorechnung weist negative Beträge aus).
  const byRate = kleinunternehmer
    ? []
    : [...byRateMap.entries()]
        .filter(([rate]) => rate > 0)
        .sort((a, b) => b[0] - a[0])
        .map(([rate, net]) => ({ rate, netCents: net, vatCents: Math.round((net * rate) / 100) }))
  return {
    docLabel: inv.type === 'cancellation' ? 'Stornorechnung' : 'Rechnung',
    invoiceNumber: inv.invoiceNumber,
    issueDate: inv.issueDate,
    draft: false,
    seller: inv.seller,
    customerName: inv.customer.name || null,
    customerAddress: inv.customer.address,
    subject: inv.subject,
    lines: inv.lines.map((l, idx) => ({ key: `l${idx}`, description: l.description, articleNumber: l.articleNumber, quantity: l.quantity, unitPriceNet: l.unitPriceNet, vatRate: l.vatRate, lineNet: l.lineNet })),
    totals: { netCents: inv.netTotal, byRate, grossCents: inv.grossTotal },
    kleinunternehmer,
    relatedInvoiceNumber: inv.relatedInvoiceNumber,
  }
}

/**
 * Rendert den Beleg-Körper aus dem normalisierten Modell. Netto/USt/Brutto kommen fertig im Modell (Live via
 * @wf/shared vatTotals, Snapshot aus den eingefrorenen Summen) — Geld nur über `formatEur`. `draft` blendet
 * Wasserzeichen + Banner ein (Rechnungs-Entwurf). Der Wrapper (Print-Seite) erzwingt das Light-Theme.
 */
export function BillingDocument({ model }: { model: BillingDocModel }) {
  const b = model.seller
  const recipientLabel = model.docLabel === 'Kostenvoranschlag' ? 'Empfänger' : 'Rechnungsempfänger'
  return (
    <article className="wf-invoice">
      {model.draft ? (
        <>
          <div className="wf-invoice__watermark" aria-hidden="true">
            Entwurf
          </div>
          <div className="wf-invoice__draftbanner" role="status">
            Entwurf – keine gültige Rechnung
          </div>
        </>
      ) : null}

      <header className="wf-invoice__head">
        <div className="wf-invoice__biz">
          <div className="wf-invoice__bizname">{b?.name ?? '—'}</div>
          {b?.legalName ? <div className="wf-invoice__bizline">{b.legalName}</div> : null}
          {b?.address ? (
            <div className="wf-invoice__bizline">
              {b.address}
              {b.zip || b.city ? `, ${b.zip ?? ''} ${b.city ?? ''}`.trimEnd() : ''}
            </div>
          ) : null}
          {b?.invPhone || b?.invEmail ? <div className="wf-invoice__bizline">{[b.invPhone, b.invEmail].filter(Boolean).join(' · ')}</div> : null}
          {b?.taxNumber || b?.vatId ? (
            <div className="wf-invoice__bizline">
              {b.taxNumber ? `St.-Nr. ${b.taxNumber}` : ''}
              {b.taxNumber && b.vatId ? ' · ' : ''}
              {b.vatId ? `USt-IdNr. ${b.vatId}` : ''}
            </div>
          ) : null}
        </div>
        <div className="wf-invoice__title">
          <div className="wf-invoice__doc">{model.docLabel}</div>
          {model.invoiceNumber ? <div className="wf-invoice__no wf-tnum">{model.invoiceNumber}</div> : null}
          {model.issueDate ? <div className="wf-invoice__date">{formatDateDE(model.issueDate)}</div> : null}
          {model.relatedInvoiceNumber ? <div className="wf-invoice__date">Storno zu Rechnung {model.relatedInvoiceNumber}</div> : null}
        </div>
      </header>

      {model.customerName ? (
        <div className="wf-invoice__to">
          <div className="wf-invoice__tolabel">{recipientLabel}</div>
          <div className="wf-invoice__toname">{model.customerName}</div>
          {model.customerAddress ? <div>{model.customerAddress}</div> : null}
        </div>
      ) : null}

      <div className="wf-invoice__veh">
        {model.subject.plate ? (
          <span className="wf-plate">
            <span className="wf-plate__eu">D</span>
            <span className="wf-plate__nr">{model.subject.plate}</span>
          </span>
        ) : null}
        <span>
          {[model.subject.vehicleModel, model.subject.orderNr ? `Auftrag ${model.subject.orderNr}` : null, model.subject.mileage != null ? `${model.subject.mileage} km` : null]
            .filter(Boolean)
            .join(' · ')}
        </span>
      </div>

      {model.subject.tasks.length > 0 ? <div className="wf-invoice__work">{model.subject.tasks.join(' · ')}</div> : null}

      <table className="wf-invoice__lines">
        <thead>
          <tr>
            <th>Artikelnr. / Bezeichnung</th>
            <th className="wf-invoice__num">Menge</th>
            <th className="wf-invoice__num">Nettopreis</th>
            <th className="wf-invoice__num" title="V = 19 %, E = 7 %, o = ohne">
              Steuer
            </th>
            <th className="wf-invoice__num">Gesamt netto</th>
          </tr>
        </thead>
        <tbody>
          {model.lines.map((l) => (
            <tr key={l.key}>
              <td>
                {l.articleNumber ? <span className="wf-tnum">{l.articleNumber} · </span> : ''}
                {l.description}
              </td>
              <td className="wf-invoice__num">{l.quantity}</td>
              <td className="wf-invoice__num wf-tnum">{formatEur(l.unitPriceNet)}</td>
              <td className="wf-invoice__num">{taxLabel(l.vatRate)}</td>
              <td className="wf-invoice__num wf-tnum">{formatEur(l.lineNet)}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="wf-invoice__totals">
        <div className="wf-invoice__total">
          <span>Gesamt netto</span>
          <span className="wf-tnum">{formatEur(model.totals.netCents)}</span>
        </div>
        {model.totals.byRate.map((group) => (
          <div key={group.rate} className="wf-invoice__taxgroup">
            <div className="wf-invoice__total">
              <span>Bemessungsgrundlage {group.rate} %</span>
              <span className="wf-tnum">{formatEur(group.netCents)}</span>
            </div>
            <div className="wf-invoice__total">
              <span>zzgl. {group.rate} % USt</span>
              <span className="wf-tnum">{formatEur(group.vatCents)}</span>
            </div>
          </div>
        ))}
        <div className="wf-invoice__total wf-invoice__total--gross">
          <span>Bruttogesamtbetrag</span>
          <span className="wf-tnum">{formatEur(model.totals.grossCents)}</span>
        </div>
      </div>

      {model.kleinunternehmer ? <p className="wf-invoice__note">Gemäß § 19 UStG wird keine Umsatzsteuer berechnet.</p> : null}
      {b?.iban ? (
        <p className="wf-invoice__pay">
          Zahlbar per Überweisung: {b.bank ? `${b.bank}, ` : ''}IBAN {b.iban}
        </p>
      ) : null}
    </article>
  )
}
